<template>
  <MasterModalBase
    width-xl
    overlay-scrollb
    :navigating="navDirection"
    :action-buttons="'true'"
    :main-transition-key="costId"
    class="cost-master-modal"
    @close-modal="close"
  >
    <template v-slot:title>
      <div>
        <span v-if="isEdit">{{ $gettext('Cost') }} {{ cost.number }}</span>
        <span v-else>{{ $gettext('New cost') }}</span>
        <span v-b-tooltip="{ trigger: 'hover', delay: { show: 500 } }" 
          :title="cost && cost.is_verified ? 
            $gettext('You have checked this cost item. You can at any time remove this check by clicking here.') : 
            $gettext('You can check this cost item to indicate that this entry has been checked and verified. It will then show a little checkmark next to it to remind you.')" 
          class="ml-2 text-info">

          <font-awesome-icon @click="verifyCost(cost)" v-if="cost && cost.is_verified" icon="fa-solid fa-badge-check" />
          <font-awesome-icon @click="verifyCost(cost)" v-if="cost && !cost.is_verified" icon="fa-regular fa-badge-check" />
        </span>
      </div>
    </template>

    <template v-slot:premiumtip>
      <GekkoButton 
        icon="fa-solid fa-circle-question"
        variant="link"
        size="lg"
        class="text-unassigned topright-btn"
        id="tt-premium-tip"
      />
      <b-tooltip :custom-class="'tooltip-padded'" target="tt-premium-tip" placement="bottom" triggers="hover">
        <div class="tooltip-title">
          {{ $gettext('Premium Tip') }}
        </div>
        <div class="tooltip-text">
          {{ $gettext('You can also add costs through email by forwarding your receipt from your account email to upload@costs.getgekko.com') }}
        </div>
      </b-tooltip>
    </template>

    <!-- <template v-slot:navbar>
      <div v-if="stage === 'edit'" class="buttons-wrap" :class="[(stage === 'edit') ? 'active' : '']">
        <div class="buttons-tt-wrap">
          <GekkoButton
            :text="$gettext('Edit')"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'edit') ? 'active' : '']"
          />
        </div>
      </div>
      <div v-if="stage === 'add'" class="buttons-wrap" :class="[(stage === 'add') ? 'active' : '']">
        <div class="buttons-tt-wrap">
          <GekkoButton
            :text="$gettext('Add')"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'add') ? 'active' : '']"
          />
        </div>
      </div>
    </template> -->

    <template v-slot:maincontent>
      <div class="master-cost-modal-view-wrap overflow-hidden">
        <div class="master-cost-modal-view">
          <div v-if="stage === 'edit' || stage === 'add'" class="view-wrap" :class="['view-wrap--' + stage]">

            <EditCost
              v-if="!loading"
              ref="cost-edit-form"
              :is-edit="(stage === 'edit')"
              @edited="costEdited"
              @created="costCreated"
              @unsaved-changes="unsavedChanges = true"
            />

          </div>
        </div>
      </div>
    </template>

    <template v-if="!isControlPanel && showNav && cost && costId" v-slot:prevnext>
      <PrevNextNavCosts 
        :item-id="costId"
        :unsaved-changes="unsavedChanges"
        @nav-direction="(dir) => { navDirection = dir; unsavedChanges = false }"
        @cost-edit-nav="costEdited"
      />
    </template>

    <template v-slot:actions>
      <div class="bottom-action-btns--wrap">
        <!-- save cost -->
        <GekkoButton
          @click="$refs['cost-edit-form'].onSubmit()"
          :text="$gettext('Save cost')"
          icon="fa-regular fa-floppy-disk"
          variant="primary"
        />
        <!-- MORE ACTIONS DROPDOWN -->
        <div v-if="showOptions && isEdit && stage === 'edit'">
          <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown" :key="'options-' + cost.id" variant="outline-primary" right no-caret dropup>
            <template #button-content>
              <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
            </template>

            <b-dropdown-item @click="copyCost">
              <font-awesome-icon icon="fa-regular fa-copy" />
              <span v-translate>Copy cost</span>
            </b-dropdown-item>
            
            <DropdownItemDelete
              :key="'dd-delete-' + cost.id"
              :item="cost"
              :item-type="'cost'"
              @item-deleted="onCostDelete"
            />

          </b-dropdown>
        </div>
      </div>
    </template>
  </MasterModalBase>
</template>
<script>
import { mapGetters, mapActions } from "vuex"
import CostService from "@/store/services/cost.service.js"
import costs from "@/mixins/costs"

import TitleBar from "@/components/TitleBar"
import GekkoButton from "@/components/GekkoButton"

import MasterModalBase from "@/components/base_components/MasterModalBase"
import EditCost from "@/components/costs/EditCost";

import DropdownItemDelete from "@/components/ui/DropdownItemDelete"

import PrevNextNavCosts from "@/components/costs/ui/PrevNextNavCosts"

export default {
  name: 'CostMasterModal',
  mixins: [costs],
  components: {
    MasterModalBase,
    GekkoButton,
    TitleBar,
    EditCost,
    PrevNextNavCosts,
    DropdownItemDelete
  },
  props: {
    stage: {
      type: String,
      default: null
    },
    costId: {
      type: [String, Number],
      default: null
    },
    showNav: {
      type: Boolean,
      default: true
    },
    showOptions: {
      type: Boolean,
      default: true
    },
    emitOnly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      actionButtons: 'edit',
      navDirection: null,
      refreshList: false,
      unsavedChanges: false,
      loading: true
    }
  },
  computed: {
    ...mapGetters({
      cost: 'costs/getCost',
      currentUser: 'main/getCurrentUser',
      visitingCompanyId: 'main/getVisitingCompanyId'
    }),
    isEdit () {
      return !!(this.cost && this.cost.id)
    },
    isControlPanel () {
      return this.$route.path.includes('control-panel')
    }
  },
  watch: {
    costId () {
      this.$nextTick(() => {
        this.setupData()
      })
    }
  },
  created () {
    this.setupData()
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  methods: {
    ...mapActions({ 
      loadCost: 'costs/loadCost',
      createCost: 'costs/createCost',
      resetCost: 'costs/resetCost',
    }),
    close () {
      this.resetCost()
      this.$emit('close')
      document.body.classList.remove('modal-on')
    },
    setupData () {
      if (this.stage !== 'create' && this.costId) {
        this.loading = true
        this.loadCost(this.costId).then(() => {
          this.loading = false
        }).catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Not found'),
            text: this.$gettext('The cost you are trying to open does not exist'),
            variant: 'warning',
            duration: 4
          })
          this.$router.push({ name: 'Costs' }).catch(() => {})
        })
      } else {
        this.loading = false
      }
    },

    // TODO refactor this
    copyCost () {
      if (!this.$store.getters['getIsPremium'] && !this.currentUser.is_accountant) {
        this.$store.commit('togglePremiumModal');
        return;
      }
      const original_number = this.cost.number
      let cost = Object.assign({}, this.cost)
      delete cost.id
      delete cost.number
      cost = this.prepareCostForUpload(cost) // mixins

      delete cost.image
      cost.date = new Date().format("Y-m-d")

      this.createCost(cost).then(() => {
        this.$store.commit('setAlert', {
          title: this.$gettext('Cost Created'), 
          text: original_number + ' ' + this.$gettext('has been copied'), 
          variant: 'success',
          duration: 4
        })
        this.$store.commit('costs/REFRESH_COST_LIST')
        // this.cost has been updated on the store
        this.$emit('created', this.cost)
        if (this.isControlPanel) {
          this.showModal = false;
          this.$emit('costCopied', this.cost.id)
        } else {
          this.$router.push({ name: 'EditCost', params: { costId: this.cost.id }, force: true });
        }

      })
    },
    costEdited () {
      if (this.emitOnly) {
        this.$emit('cost-edited', false)
      } else {
        this.$store.commit('costs/REFRESH_COST_LIST')
        this.$router.push({ name: 'Costs' }).catch(() => {})
      }
    },
    costCreated () {
      if (this.emitOnly) {
        if (this.isControlPanel) this.$router.push({ name: 'ControlPanelCosts', params: { customerId: this.visitingCompanyId } })
        else this.$emit('cost-created', false)
      } else {
        this.$store.commit('costs/REFRESH_COST_LIST')
        this.$router.push({ name: 'Costs' }).catch(() => {})
      }
    },
    onCostDelete () {
      if (this.emitOnly) {
        this.$emit('cost-deleted', false)
      } else {
        this.$store.commit('costs/REFRESH_COST_LIST')
        this.goToCostTable();
        this.$emit('delete', this.cost.id);
      }
    },
    goToCostTable () {
      if (this.isControlPanel) {
        this.$router.push({ name: 'ControlPanelCosts', params: { customerId: this.visitingCompanyId } }).catch(() => {})
      } else {
        this.$router.push({ name: 'Costs' }).catch(() => {})
      }
    },
    verifyCost(item) {
        item.is_verified = !item.is_verified
        
        CostService.partialUpdate(item.id, item).then(() => {
          this.$refs['cost-edit-form'].setupData()
          this.$store.commit('costs/REFRESH_COST_LIST')
        }).catch(() => {
            this.$store.commit('setAlert', {
                title: this.$gettext("Error"),
                text: this.$gettext('Unable to verify the cost. Please try again later.'),
                variant: "danger",
                duration: 4
            })
            
            item.is_verified = !item.is_verified // revert change
        })
    }
  }
}
</script>
<style lang="scss">
  .cost-master-modal {
    .master-cost-modal-view-wrap {
      width: 100%;
      height: 100%;
      position: relative;
      min-height: 100%;
      .master-cost-modal-view {
        height: 100%;
        display: block;
        .view-wrap {
          height: 100%;
          &--edit {
          }
        }
      }
    }
  }
</style>
